import {
  ArrowRight,
  Building2,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  MailPlus,
  MessageSquareMore,
  Plus,
  Users,
  X,
} from "lucide-react";
import { addMonths, format } from "date-fns";
import { nl } from "date-fns/locale";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import api from "../api/client";
import { ErrorNotice, LoadingState, SuccessNotice } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { useWorkspace } from "../context/WorkspaceContext";
import type { LeaveEntry, Paginated, Team } from "../types/api";
import { unwrapResults } from "../types/api";
import { formatDate, formatMinutes } from "../utils/format";

type TeamCalendarDay = {
  date: string;
  school_holidays: Array<{ id: number; name: string; region: string }>;
  public_holidays: Array<{ id: number; name: string }>;
  absences: Array<{
    entry_id: number;
    user_id: number;
    display_name: string;
    label: string;
    status: LeaveEntry["status"];
    requested_minutes: number;
  }>;
  occupancy: {
    capacity: number;
    approved_absent: number;
    requested_absent: number;
    available: number;
  };
};

export default function TeamPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { organizationId, organization, capabilities } = useWorkspace();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const [days, setDays] = useState<TeamCalendarDay[]>([]);
  const [approvals, setApprovals] = useState<LeaveEntry[]>([]);
  const [month, setMonth] = useState(new Date());
  const [view, setView] = useState(
    searchParams.get("view") === "approvals" ? "approvals" : "planning",
  );
  const [teamName, setTeamName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [comments, setComments] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadTeams = useCallback(async () => {
    if (!organizationId) return;
    const response = await api.get<Paginated<Team> | Team[]>("/teams/");
    const rows = unwrapResults(response.data).filter(
      (row) => row.organization === organizationId,
    );
    setTeams(rows);
    setSelectedTeam((current) =>
      current && rows.some((row) => row.id === current) ? current : rows[0]?.id ?? null,
    );
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    void Promise.all([
      loadTeams(),
      capabilities.canApproveLeave
        ? api
            .get(`/organizations/${organizationId}/approvals/`)
            .then((response) => setApprovals(response.data.results))
        : Promise.resolve(),
    ])
      .catch(() => setError("De teamgegevens konden niet worden geladen."))
      .finally(() => setLoading(false));
  }, [organizationId, capabilities.canApproveLeave, loadTeams]);

  useEffect(() => {
    if (!selectedTeam) {
      setDays([]);
      return;
    }
    void api
      .get(`/teams/calendar/?team=${selectedTeam}&date=${format(month, "yyyy-MM-dd")}`)
      .then((response) => setDays(response.data.days))
      .catch(() => setError("De teamplanning kon niet worden geladen."));
  }, [selectedTeam, month]);

  const today = format(new Date(), "yyyy-MM-dd");
  const todayRow = days.find((day) => day.date === today);
  const approvedMinutes = useMemo(
    () =>
      days
        .flatMap((day) => day.absences)
        .filter((row) => row.status === "approved")
        .reduce((sum, row) => sum + row.requested_minutes, 0),
    [days],
  );
  const averageAvailability = days.length
    ? Math.round(
        days.reduce(
          (sum, day) =>
            sum +
            (day.occupancy.capacity
              ? (day.occupancy.available / day.occupancy.capacity) * 100
              : 100),
          0,
        ) / days.length,
      )
    : 100;

  const setActiveView = (next: string) => {
    setView(next);
    setSearchParams(next === "approvals" ? { view: "approvals" } : {});
  };

  const createTeam = async (event: FormEvent) => {
    event.preventDefault();
    if (!organizationId || !teamName.trim()) return;
    try {
      await api.post("/teams/", {
        organization: organizationId,
        name: teamName.trim(),
      });
      setTeamName("");
      setSuccess("Team aangemaakt.");
      await loadTeams();
    } catch {
      setError(
        "Het team kon niet worden aangemaakt. Team ondersteunt één team; Business meerdere.",
      );
    }
  };

  const invite = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedTeam || !inviteEmail) return;
    try {
      await api.post(`/teams/${selectedTeam}/invites/`, {
        team: selectedTeam,
        email: inviteEmail,
        role: "member",
      });
      setInviteEmail("");
      setSuccess("Uitnodiging verstuurd.");
    } catch {
      setError("De uitnodiging kon niet worden verstuurd.");
    }
  };

  const decide = async (
    entry: LeaveEntry,
    target: "approved" | "rejected" | "needs_info",
  ) => {
    try {
      await api.post(`/leave/entries/${entry.id}/transition/`, {
        target_status: target,
        comment: comments[entry.id] ?? "",
      });
      setComments((current) => ({ ...current, [entry.id]: "" }));
      setSuccess("De aanvraag is bijgewerkt.");
      if (organizationId) {
        const response = await api.get(`/organizations/${organizationId}/approvals/`);
        setApprovals(response.data.results);
      }
    } catch {
      setError("De aanvraag kon niet worden bijgewerkt.");
    }
  };

  if (!organizationId || !organization) {
    return (
      <div className="upgrade-page">
        <PageHeader
          title="Team & fiatteren"
          description="Teamfunctionaliteit hoort bij een betaald pakket."
        />
        <section className="upgrade-panel">
          <span className="upgrade-icon">
            <Building2 size={28} />
          </span>
          <h2>Werk samen vanuit één verlofplanning</h2>
          <p>Nodig medewerkers uit, keur aanvragen goed en bewaak de bezetting.</p>
          <Link className="button primary" to="/app/settings/abonnement">
            Bekijk Team <ArrowRight size={17} />
          </Link>
        </section>
      </div>
    );
  }
  if (loading) return <LoadingState label="Team laden..." />;

  return (
    <div className="team-page">
      <PageHeader
        eyebrow={organization.name}
        title="Team & fiatteren"
        description="Afwezigheid, aanvragen en bezetting in één werkoverzicht."
        actions={
          <div className="team-selector">
            <Users size={17} />
            <select
              value={selectedTeam ?? ""}
              onChange={(event) => setSelectedTeam(Number(event.target.value))}
            >
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        }
      />
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}

      <section className="metric-grid team-metrics">
        <article className="metric-card">
          <span className="metric-icon">
            <MessageSquareMore size={20} />
          </span>
          <div>
            <span>Openstaande aanvragen</span>
            <strong>{approvals.length}</strong>
            <small>Wachten op beoordeling</small>
          </div>
        </article>
        <article className="metric-card">
          <span className="metric-icon metric-mint">
            <Users size={20} />
          </span>
          <div>
            <span>Vandaag afwezig</span>
            <strong>{todayRow?.occupancy.approved_absent ?? 0}</strong>
            <small>
              Van{" "}
              {todayRow?.occupancy.capacity ??
                teams.find((row) => row.id === selectedTeam)?.memberships.length ??
                0}{" "}
              medewerkers
            </small>
          </div>
        </article>
        <article className="metric-card">
          <span className="metric-icon metric-violet">
            <CalendarDays size={20} />
          </span>
          <div>
            <span>Goedgekeurd deze maand</span>
            <strong>{formatMinutes(approvedMinutes)}</strong>
            <small>Alle afwezigheidsdagen</small>
          </div>
        </article>
        <article className="metric-card">
          <span className="metric-icon metric-amber">
            <Check size={20} />
          </span>
          <div>
            <span>Gemiddelde bezetting</span>
            <strong>{averageAvailability}%</strong>
            <small>Op basis van goedgekeurd verlof</small>
          </div>
        </article>
      </section>

      <div className="section-tabs" role="tablist">
        <button
          className={view === "planning" ? "active" : ""}
          onClick={() => setActiveView("planning")}
        >
          Teamplanning
        </button>
        <button
          className={view === "approvals" ? "active" : ""}
          onClick={() => setActiveView("approvals")}
        >
          Fiatteren <span>{approvals.length}</span>
        </button>
        {capabilities.canManageTeams && (
          <button
            className={view === "manage" ? "active" : ""}
            onClick={() => setActiveView("manage")}
          >
            Beheer
          </button>
        )}
      </div>

      {view === "planning" && (
        <section className="surface team-calendar-surface">
          <div className="surface-heading">
            <h2>{format(month, "MMMM yyyy", { locale: nl })}</h2>
            <div className="year-stepper">
              <button
                className="icon-button"
                aria-label="Vorige maand"
                title="Vorige maand"
                onClick={() => setMonth((value) => addMonths(value, -1))}
              >
                <ChevronLeft size={18} />
              </button>
              <button className="button subtle" onClick={() => setMonth(new Date())}>
                Vandaag
              </button>
              <button
                className="icon-button"
                aria-label="Volgende maand"
                title="Volgende maand"
                onClick={() => setMonth((value) => addMonths(value, 1))}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
          <div className="team-calendar-list">
            {days
              .filter(
                (day) =>
                  day.absences.length ||
                  day.school_holidays.length ||
                  day.public_holidays.length,
              )
              .map((day) => (
                <article
                  key={day.date}
                  className={`team-calendar-row${
                    day.school_holidays.length ? " is-school-holiday" : ""
                  }`}
                >
                  <div className="team-day-label">
                    <strong>{formatDate(day.date, "d")}</strong>
                    <span>{formatDate(day.date, "EEE")}</span>
                  </div>
                  <div className="team-day-content">
                    {day.school_holidays.map((holiday) => (
                      <span key={holiday.id} className="calendar-layer-label school">
                        {holiday.name}
                      </span>
                    ))}
                    {day.public_holidays.map((holiday) => (
                      <span key={holiday.id} className="calendar-layer-label holiday">
                        {holiday.name}
                      </span>
                    ))}
                    {day.absences.map((absence) => (
                      <span
                        key={absence.entry_id}
                        className={`absence-bar status-${absence.status}`}
                      >
                        <span className="avatar mini">
                          {absence.display_name.slice(0, 2).toUpperCase()}
                        </span>
                        {absence.display_name} · {absence.label}
                      </span>
                    ))}
                  </div>
                  <div className="occupancy-cell">
                    <strong>
                      {day.occupancy.available}/{day.occupancy.capacity}
                    </strong>
                    <span>beschikbaar</span>
                  </div>
                </article>
              ))}
          </div>
        </section>
      )}

      {view === "approvals" && (
        <section className="approval-layout">
          {approvals.length === 0 ? (
            <div className="surface empty-state">
              <Check size={28} />
              <h2>Alles is bijgewerkt</h2>
              <p>Er wachten geen aanvragen op fiattering.</p>
            </div>
          ) : (
            approvals.map((entry) => (
              <article className="surface approval-card" key={entry.id}>
                <div className="approval-card-header">
                  <span className="avatar">
                    {entry.user_name.slice(0, 2).toUpperCase()}
                  </span>
                  <div>
                    <h2>{entry.user_name}</h2>
                    <p>
                      {entry.leave_type_name} · {formatDate(entry.start_date)}
                      {entry.end_date !== entry.start_date
                        ? ` t/m ${formatDate(entry.end_date)}`
                        : ""}
                    </p>
                  </div>
                  <StatusBadge status={entry.status} />
                </div>
                <div className="approval-impact">
                  <div>
                    <span>Aangevraagd</span>
                    <strong>{formatMinutes(entry.requested_minutes)}</strong>
                  </div>
                  <div>
                    <span>Werkdagen</span>
                    <strong>
                      {entry.request_days.filter((day) => day.requested_minutes)
                        .length || 1}
                    </strong>
                  </div>
                  <div>
                    <span>Schoolvakantie</span>
                    <strong>
                      {entry.request_days.some((day) => day.school_holiday_names.length)
                        ? "Ja, informatief"
                        : "Nee"}
                    </strong>
                  </div>
                </div>
                {entry.reason && (
                  <p className="approval-reason">
                    <strong>Reden</strong> {entry.reason}
                  </p>
                )}
                <label>
                  <span>Toelichting bij besluit</span>
                  <textarea
                    value={comments[entry.id] ?? ""}
                    onChange={(event) =>
                      setComments((current) => ({
                        ...current,
                        [entry.id]: event.target.value,
                      }))
                    }
                    rows={2}
                  />
                </label>
                <div className="approval-actions">
                  <button
                    className="button success"
                    onClick={() => void decide(entry, "approved")}
                  >
                    <Check size={17} /> Goedkeuren
                  </button>
                  <button
                    className="button subtle"
                    onClick={() => void decide(entry, "needs_info")}
                  >
                    <MessageSquareMore size={17} /> Vraag om toelichting
                  </button>
                  <button
                    className="button danger"
                    onClick={() => void decide(entry, "rejected")}
                  >
                    <X size={17} /> Afwijzen
                  </button>
                </div>
              </article>
            ))
          )}
        </section>
      )}

      {view === "manage" && capabilities.canManageTeams && (
        <section className="management-grid">
          <form className="surface management-form" onSubmit={createTeam}>
            <h2>Team toevoegen</h2>
            <label>
              <span>Teamnaam</span>
              <input
                value={teamName}
                onChange={(event) => setTeamName(event.target.value)}
                placeholder="Bijvoorbeeld Product"
              />
            </label>
            <button className="button primary" type="submit">
              <Plus size={17} /> Team maken
            </button>
          </form>
          <form className="surface management-form" onSubmit={invite}>
            <h2>Medewerker uitnodigen</h2>
            <label>
              <span>E-mailadres</span>
              <input
                type="email"
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
                placeholder="naam@bedrijf.nl"
              />
            </label>
            <button className="button primary" type="submit">
              <MailPlus size={17} /> Uitnodigen
            </button>
          </form>
        </section>
      )}
    </div>
  );
}
